import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const FoodsFromPlant: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/FoodsFromPlant.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/FoodsFromPlant.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Food We Get from Plants – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Discover the variety of food we get from plants in Class 3 Science. Learn about fruits, vegetables, cereals, pulses, oils, and more with simple explanations at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🥦 Chapter 7: Food We Get from Plants</h1>

                            <h2>✨ Introduction</h2>
                            <p>Plants are very useful to us. They not only give us oxygen but also provide us with a <strong>variety of food</strong>. We eat different parts of plants like <strong>roots, stems, leaves, fruits, and seeds</strong>.</p>

                            <h2>🥕 Parts of Plants We Eat</h2>
                            <h3>🌿 1. Roots</h3>
                            <p>Roots grow under the soil. We eat the roots of some plants.</p>
                            <ul>
                                <li><strong>Examples:</strong> Carrot, Beetroot, Radish, Turnip</li>
                            </ul>

                            <h3>🫔 2. Stems</h3>
                            <p>Some stems are soft and green, others are thick and underground.</p>
                            <ul>
                                <li><strong>Examples:</strong> Sugarcane, Potato (stem), Ginger (stem)</li>
                            </ul>

                            <h3>🥬 3. Leaves</h3>
                            <p>Many green vegetables are leaves rich in vitamins.</p>
                            <ul>
                                <li><strong>Examples:</strong> Spinach, Lettuce, Cabbage, Coriander</li>
                            </ul>

                            <h3>🍇 4. Fruits</h3>
                            <p>Fruits are juicy and sweet. They are good for health.</p>
                            <ul>
                                <li><strong>Examples:</strong> Mango, Apple, Banana, Orange</li>
                            </ul>

                            <h3>🌻 5. Seeds</h3>
                            <p>We eat seeds of some plants and get oils from them.</p>
                            <ul>
                                <li><strong>Examples:</strong> Rice, Wheat, Peas, Groundnut, Mustard</li>
                            </ul>

                            <h2>🧦 Plants That Give Us Oil</h2>
                            <ul>
                                <li><strong>Mustard</strong> → Mustard oil</li>
                                <li><strong>Groundnut</strong> → Groundnut oil</li>
                                <li><strong>Sunflower</strong> → Sunflower oil</li>
                                <li><strong>Coconut</strong> → Coconut oil</li>
                            </ul>

                            <h2>🍬 Plants That Give Us Sugar and Spices</h2>
                            <ul>
                                <li><strong>Sugarcane</strong> – Used to make sugar</li>
                                <li><strong>Beetroot</strong> – Also used for sugar</li>
                                <li><strong>Spices</strong> – Turmeric, Clove, Cardamom, Cinnamon</li>
                            </ul>

                            <h2>☕ Plants That Give Us Beverages</h2>
                            <ul>
                                <li><strong>Tea</strong> – From tea leaves</li>
                                <li><strong>Coffee</strong> – From coffee seeds</li>
                                <li><strong>Cocoa</strong> – Used to make chocolate</li>
                            </ul>

                            <h2>🌾 Cereals and Pulses</h2>
                            <h3>🌾 Cereals</h3>
                            <p>Give us energy.</p>
                            <ul>
                                <li>Examples: Rice, Wheat, Maize, Barley</li>
                            </ul>

                            <h3>🫘 Pulses</h3>
                            <p>Rich in protein. Help us grow.</p>
                            <ul>
                                <li>Examples: Kidney beans, Gram, Moong, Masoor</li>
                            </ul>

                            <h2>✅ Summary Table</h2>
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Plant Part</th>
                                        <th>Examples</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Roots</td>
                                        <td>Carrot, Radish, Beetroot</td>
                                    </tr>
                                    <tr>
                                        <td>Stems</td>
                                        <td>Sugarcane, Potato, Ginger</td>
                                    </tr>
                                    <tr>
                                        <td>Leaves</td>
                                        <td>Spinach, Cabbage, Mint</td>
                                    </tr>
                                    <tr>
                                        <td>Fruits</td>
                                        <td>Apple, Mango, Banana</td>
                                    </tr>
                                    <tr>
                                        <td>Seeds</td>
                                        <td>Rice, Wheat, Peas</td>
                                    </tr>
                                    <tr>
                                        <td>Oils</td>
                                        <td>Mustard, Coconut, Groundnut</td>
                                    </tr>
                                    <tr>
                                        <td>Beverages</td>
                                        <td>Tea, Coffee, Cocoa</td>
                                    </tr>
                                    <tr>
                                        <td>Spices</td>
                                        <td>Turmeric, Clove, Cardamom</td>
                                    </tr>
                                </tbody>
                            </table>

                            <h2>🌿 Why is Plant Food Important?</h2>
                            <ul>
                                <li>Keeps us healthy</li>
                                <li>Gives us energy</li>
                                <li>Helps us grow</li>
                                <li>Fights diseases</li>
                                <li>Adds taste to food</li>
                            </ul>

                            <div className="activity">
                                <h3>🧠 Fun Activity</h3>
                                <ul>
                                    <li>Make a chart of “Food from Plants” with pictures</li>
                                    <li>Visit a kitchen garden or vegetable market</li>
                                    <li>Identify parts of plants you eat in a meal</li>
                                </ul>
                            </div>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🥦 Class 3 Science – Chapter 7: Food We Get from Plants</h1>

                        <h2>✨ Introduction</h2>
                        <p>Plants are very useful to us. They not only give us oxygen but also provide us with a <strong>variety of food</strong>. We eat different parts of plants like <strong>roots, stems, leaves, fruits, and seeds</strong>.</p>

                        <h2>🥕 Parts of Plants We Eat</h2>
                        <h3>🌿 1. Roots</h3>
                        <p>Roots grow under the soil. We eat the roots of some plants.</p>
                        <ul>
                            <li><strong>Examples:</strong> Carrot, Beetroot, Radish, Turnip</li>
                        </ul>

                        <h3>🫔 2. Stems</h3>
                        <p>Some stems are soft and green, others are thick and underground.</p>
                        <ul>
                            <li><strong>Examples:</strong> Sugarcane, Potato (stem), Ginger (stem)</li>
                        </ul>

                        <h3>🥬 3. Leaves</h3>
                        <p>Many green vegetables are leaves rich in vitamins.</p>
                        <ul>
                            <li><strong>Examples:</strong> Spinach, Lettuce, Cabbage, Coriander</li>
                        </ul>

                        <h3>🍇 4. Fruits</h3>
                        <p>Fruits are juicy and sweet. They are good for health.</p>
                        <ul>
                            <li><strong>Examples:</strong> Mango, Apple, Banana, Orange</li>
                        </ul>

                        <h3>🌻 5. Seeds</h3>
                        <p>We eat seeds of some plants and get oils from them.</p>
                        <ul>
                            <li><strong>Examples:</strong> Rice, Wheat, Peas, Groundnut, Mustard</li>
                        </ul>

                        <h2>🧦 Plants That Give Us Oil</h2>
                        <ul>
                            <li><strong>Mustard</strong> → Mustard oil</li>
                            <li><strong>Groundnut</strong> → Groundnut oil</li>
                            <li><strong>Sunflower</strong> → Sunflower oil</li>
                            <li><strong>Coconut</strong> → Coconut oil</li>
                        </ul>

                        <h2>🍬 Plants That Give Us Sugar and Spices</h2>
                        <ul>
                            <li><strong>Sugarcane</strong> – Used to make sugar</li>
                            <li><strong>Beetroot</strong> – Also used for sugar</li>
                            <li><strong>Spices</strong> – Turmeric, Clove, Cardamom, Cinnamon</li>
                        </ul>

                        <h2>☕ Plants That Give Us Beverages</h2>
                        <ul>
                            <li><strong>Tea</strong> – From tea leaves</li>
                            <li><strong>Coffee</strong> – From coffee seeds</li>
                            <li><strong>Cocoa</strong> – Used to make chocolate</li>
                        </ul>

                        <h2>🌾 Cereals and Pulses</h2>
                        <h3>🌾 Cereals</h3>
                        <p>Give us energy.</p>
                        <ul>
                            <li>Examples: Rice, Wheat, Maize, Barley</li>
                        </ul>

                        <h3>🩘 Pulses</h3>
                        <p>Rich in protein. Help us grow.</p>
                        <ul>
                            <li>Examples: Kidney beans, Gram, Moong, Masoor</li>
                        </ul>

                        <h2>✅ Summary Table</h2>
                        <table>
                            <tr>
                                <th>Plant Part</th>
                                <th>Examples</th>
                            </tr>
                            <tr>
                                <td>Roots</td>
                                <td>Carrot, Radish, Beetroot</td>
                            </tr>
                            <tr>
                                <td>Stems</td>
                                <td>Sugarcane, Potato, Ginger</td>
                            </tr>
                            <tr>
                                <td>Leaves</td>
                                <td>Spinach, Cabbage, Mint</td>
                            </tr>
                            <tr>
                                <td>Fruits</td>
                                <td>Apple, Mango, Banana</td>
                            </tr>
                            <tr>
                                <td>Seeds</td>
                                <td>Rice, Wheat, Peas</td>
                            </tr>
                            <tr>
                                <td>Oils</td>
                                <td>Mustard, Coconut, Groundnut</td>
                            </tr>
                            <tr>
                                <td>Beverages</td>
                                <td>Tea, Coffee, Cocoa</td>
                            </tr>
                            <tr>
                                <td>Spices</td>
                                <td>Turmeric, Clove, Cardamom</td>
                            </tr>
                        </table>

                        <h2>🌿 Why is Plant Food Important?</h2>
                        <ul>
                            <li>Keeps us healthy</li>
                            <li>Gives us energy</li>
                            <li>Helps us grow</li>
                            <li>Fights diseases</li>
                            <li>Adds taste to food</li>
                        </ul>

                        <div className="activity">
                            <h3>🧠 Fun Activity</h3>
                            <ul>
                                <li>Make a chart of “Food from Plants” with pictures</li>
                                <li>Visit a kitchen garden or vegetable market</li>
                                <li>Identify parts of plants you eat in a meal</li>
                            </ul>
                        </div>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default FoodsFromPlant;
